import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { DailyStatus } from '@/types/task';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

export function useDailyStatus(date?: Date) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState<DailyStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const dateString = date ? format(date, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

  const fetchStatus = useCallback(async () => {
    if (!user) {
      setStatus(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('daily_status')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', dateString)
        .maybeSingle();

      if (error) throw error;
      setStatus(data as DailyStatus | null);
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  }, [user, dateString]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const updateStatus = async (content: string) => {
    if (!user) return;

    try {
      if (status) {
        const { error } = await supabase
          .from('daily_status')
          .update({ content })
          .eq('id', status.id);

        if (error) throw error;
        setStatus(prev => prev ? { ...prev, content } : null);
      } else {
        const { data, error } = await supabase
          .from('daily_status')
          .insert({
            user_id: user.id,
            date: dateString,
            content,
          })
          .select()
          .single();

        if (error) throw error;
        setStatus(data as DailyStatus);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error saving status',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return {
    status,
    loading,
    updateStatus,
    refetch: fetchStatus,
  };
}
